import { useMemo } from "react";
import { useCmsContext } from "@rotorstudios/fe-cms";
import { useQuery } from "@tanstack/react-query";

import type { iHybridEngines } from "./types";
import {
  CMS_ALL_ENGINES,
  CMS_DATA_SOURCES,
  CMS_DATASOURCES_HYBRIDENGINES,
} from "./static";
import { useFetchCMSData } from "./useFetchCMSData";

const CACHE_TIME = 5 * 60 * 1000; // 5 minutes in ms

type UseHybridEnginesProps = {
  vehicleEngine?: string;
};

const useHybridEngines = ({ vehicleEngine }: UseHybridEnginesProps) => {
  const { fetchData } = useFetchCMSData();
  const { siloManifest } = useCmsContext();

  const path = siloManifest?.[CMS_DATA_SOURCES]?.get(CMS_DATASOURCES_HYBRIDENGINES);

  // ********************
  // Hybrid Engines Data
  // ********************
  const getHybridEnginesData = async (): Promise<iHybridEngines> => {
    if (!path) throw new Error("Hybrid Engines data source not found in silo manifest");
    const raw = await fetchData(path);
    return raw?.data;
  };

  const { data: hybridEngines } = useQuery({
    queryKey: ["hybridEngines", path],
    queryFn: () => getHybridEnginesData(),
    enabled: Boolean(path),
    staleTime: CACHE_TIME,
  });

  const isHybridEngine = useMemo(
    () =>
      hybridEngines && vehicleEngine
        ? hybridEngines?.hybridEngineNames?.includes(vehicleEngine || CMS_ALL_ENGINES)
        : false,
    [hybridEngines, vehicleEngine],
  );

  return {
    isHybridEngine,
  };
};

export default useHybridEngines;
